/** Copyright 2024 SAP SE or an SAP affiliate company. All rights reserved. */
import { TaskDto, UserDto } from '@/commons/dtos';
import CreatedUserCard from '@/components/additionalUser/CreatedUserCard';
import CreateUserButtonCard from '@/components/additionalUser/CreateUserButtonCard';
import CreateUserFormDialog from '@/components/CreateUserFormDialog/CreateUserFormDialog';
import { useAppDispatch, useAppSelector } from '@/hooks';
import useOpen from '@/hooks/useOpen';
import { useTranslations } from '@/hooks/useTranslations';
import { freApi, useDeleteUserMutation, useGetUsersQuery } from '@/store/apis';
import { createStyles } from '@/utils/styles';
import { FlexBox, Text, Title } from '@ui5/webcomponents-react';
import { ThemingParameters } from '@ui5/webcomponents-react-base';
import { useState } from 'react';

export default function AdditionalUserLayout({ content }: Readonly<{ content: TaskDto }>) {
    const dispatch = useAppDispatch();
    const [isOpen, toggle] = useOpen();
    const { t } = useTranslations();
    const [isEditingUserIndex, setIsEditingUserIndex] = useState<number | null>(null);
    const currentRunId = useAppSelector(state => state.productList.currentRunId);
    const { data: users = [] } = useGetUsersQuery(currentRunId, {
        skip: !currentRunId,
    });
    const [deleteUser, { isLoading: isDeleting }] = useDeleteUserMutation();
    const editingUser = isEditingUserIndex !== null ? users[isEditingUserIndex] : undefined;
    const isExceedingUserLimit = users.length >= 10;

    return (
        <div style={styles.wrapper} data-testid="additionalUserLayout">
            <FlexBox direction="Column" style={styles.headerWrapper}>
                <Title level="H4" size="H4">
                    {t({ key: content.step.translationTitleKey, defaultText: content.step.title })}
                </Title>
                <Text>{t({ key: content.translationTitleKey, defaultText: content.title })}</Text>
            </FlexBox>

            <div style={{ display: 'grid', justifyContent: 'center', gap: 16, gridTemplateColumns: 'repeat(auto-fit, 316px)' }}>
                <CreateUserButtonCard
                    onClick={() => {
                        setIsEditingUserIndex(null);
                        toggle();
                    }}
                    disabled={isExceedingUserLimit}
                />
                {users.map((userDto, i) => (
                    <CreatedUserCard
                        key={i + (userDto.id ?? '') + userDto.userName}
                        userDto={userDto}
                        isRemoving={isDeleting}
                        onRemove={async () => {
                            if (userDto.id) {
                                try {
                                    await deleteUser(userDto.id).unwrap();
                                } catch (e) {
                                    console.error(e);
                                    return;
                                }
                            }

                            dispatch(freApi.util.updateQueryData('getUsers', currentRunId, draft => draft.filter((_, i2) => i !== i2)));
                        }}
                        onEdit={() => {
                            setIsEditingUserIndex(i);
                            toggle();
                        }}
                    />
                ))}
            </div>
            {isOpen && (
                <CreateUserFormDialog
                    onClose={toggle}
                    onSubmit={(userDto: UserDto) => {
                        dispatch(
                            freApi.util.updateQueryData('getUsers', currentRunId, draft => {
                                if (isEditingUserIndex === null) {
                                    draft.push(userDto);
                                } else {
                                    draft[isEditingUserIndex] = userDto;
                                    setIsEditingUserIndex(null);
                                }
                            })
                        );
                    }}
                    defaultValues={editingUser}
                    users={users}
                />
            )}
        </div>
    );
}

const styles = createStyles({
    wrapper: {
        padding: '40px 16px',
        background: ThemingParameters.sapGroup_ContentBackground,
        borderRadius: ThemingParameters.sapElement_BorderCornerRadius,
        boxShadow: ThemingParameters.sapContent_Shadow0,
        marginBottom: 48,
    },

    headerWrapper: {
        gap: 8,
        textAlign: 'center',
        marginBottom: 32,
    },
});
